import React, { useEffect, useMemo, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, Icon, Tooltip } from "@mui/material";
import cx from "classnames";
// Components
import { Table, SearchInput, Toolbar, Button } from "Components/uiControls";
import { NotificationManager } from "Components/Notifications";
import NewIntegrationWizard from "./components/newIntegrationWizard";
import DeleteIntegrations from "./components/deleteIntegrations";
import EditIntegration from "./components/editIntegration";
import IntegrationMenu from "./components/integrationMenu";
import LinkedVehiclesConsole from "./components/linkedVehiclesConsole";
import CopyApiKey from "./components/copyApiKey";
import { GRID_CLOCK_MS, GRID_POLL_MS, SYNC_MAX_WAIT_MS, SYNC_POLL_MS, sinceText, telemetryState, zoneLabel } from "./transmissions";
// Actions
import { clearRedux } from "Redux/actions";
import { accountAppsSearch, accountAppsSearchUnits, accountAppsTransmissionStats, appsSyncFinished, appsSyncProgress, clearAppsFlash, openAppsConsole, openAppsDelete, openAppsWizard, reset } from "./redux/slice";
import "./styles.scss";

const FLASH_MS = 2600;

const AppsTab = (props) => {
  const { intl: { messages } } = props;
  const dispatch = useDispatch();
  const tableRef = useRef();

  const { loading, integrations, error, openWizard, openDelete, deleteResponse, syncingIds, syncProgress, flashId, console: consoleState, edit } = useSelector((state) => state.accountAppsRedux);
  const companyIntegrationSync = useSelector((state) => state.catalogsRedux.companyIntegrationSync);

  const [searchText, setSearchText] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  // The transmission indicator ages on its own (clock) and follows the transmission log (silent reload)
  const [now, setNow] = useState(() => Date.now());
  // Syncs whose result was already announced (push or poll), so the other path never repeats the toast
  const announcedSyncs = useRef(new Set());

  useEffect(() => {
    dispatch(accountAppsSearch());
    const clock = setInterval(() => setNow(Date.now()), GRID_CLOCK_MS);
    const poll = setInterval(() => dispatch(accountAppsSearch({ silent: true })), GRID_POLL_MS);
    return () => {
      clearInterval(clock);
      clearInterval(poll);
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    if (!error) return;
    NotificationManager.error(messages["errorMessage"], messages["error"], 3000, null, null, "warning");
  }, [error]);

  // Highlight the row the wizard just added
  useEffect(() => {
    if (!flashId) return;
    const timer = setTimeout(() => dispatch(clearAppsFlash()), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashId]);

  useEffect(() => {
    if (!deleteResponse) return;
    tableRef?.current?.setSelectedItems([]);
    setSelectedItems([]);
    if (deleteResponse.error) {
      NotificationManager.error(deleteResponse.error === true ? messages["errorMessage"] : deleteResponse.error, messages["error"], 3000, null, null, "warning");
      dispatch(accountAppsSearch());
      return;
    }
    const message = deleteResponse.deleted === 1
      ? messages["accountApps_deleted"]
      : messages["accountApps_deletedMany"].replace("_COUNT_", deleteResponse.deleted);
    NotificationManager.success(message, messages["success"], 3000, null, null, "success");
  }, [deleteResponse]);

  // Ends "Syncing vehicles" with the result of the sync, from its push or from the API (lastSync), announced once
  const finishSync = (id, { status, matched }) => {
    dispatch(appsSyncFinished(id));
    // The open console of that integration shows what the sync just linked, whoever started it (grid, API or cron)
    if (consoleState.open && consoleState.integrationId === id) {
      dispatch(accountAppsSearchUnits(id));
      dispatch(accountAppsTransmissionStats({ companyIntegrationId: id }));
    }
    if (announcedSyncs.current.has(id)) return;
    announcedSyncs.current.add(id);
    const appName = integrations.find((item) => item.id === id)?.integrationName || "";
    if (status === "SUCCESS") {
      // The platform toast as is: clicking it opens the console with the linked vehicles (like the import toasts)
      const text = (Number(matched) === 1 ? messages["accountApps_syncDoneOne"] : messages["accountApps_syncDone"].replace("_COUNT_", matched || 0)).replace("_APP_", appName);
      NotificationManager.success(text, messages["success"], 9000, () => dispatch(openAppsConsole(id)), null, "success");
    } else {
      NotificationManager.error(messages["accountApps_syncFailed"].replace("_APP_", appName), messages["error"], 5000, null, null, "warning");
    }
  };

  // Push notification (COMPANY_INTEGRATION_SYNC) sent when a sync started from the UI finishes
  useEffect(() => {
    if (!companyIntegrationSync) return;
    const { companyIntegrationId, status, matched, processed } = companyIntegrationSync;
    const id = Number(companyIntegrationId);
    dispatch(clearRedux("CATALOG_REDUX", { companyIntegrationSync: null }));
    // Between CoastPay pages the sync tells how many vehicles it processed: only the percentage moves
    if (status === "RUNNING") {
      dispatch(appsSyncProgress({ id, processed }));
      return;
    }
    finishSync(id, { status, matched });
  }, [companyIntegrationSync]);

  // The push is best effort (the browser may miss it): while a sync started here runs, the grid asks for its
  // result every few seconds and ends it with the integration's lastSync
  const syncing = syncingIds.length > 0;
  useEffect(() => {
    if (!syncing) return undefined;
    const poll = setInterval(() => dispatch(accountAppsSearch({ silent: true })), SYNC_POLL_MS);
    return () => clearInterval(poll);
  }, [syncing]);

  useEffect(() => {
    syncingIds.forEach((id) => {
      const progress = syncProgress[id] || {};
      const lastSync = integrations.find((item) => item.id === id)?.lastSync;
      if (lastSync?.finishedOn && lastSync.finishedOn !== (progress.since || null)) {
        finishSync(id, lastSync);
      } else if (progress.startedAt && now - progress.startedAt > SYNC_MAX_WAIT_MS) {
        // Nothing after half an hour: stop waiting; the vehicles appear when the sync ends
        dispatch(appsSyncFinished(id));
        const appName = integrations.find((item) => item.id === id)?.integrationName || "";
        NotificationManager.info(messages["accountApps_syncSlow"].replace("_APP_", appName), messages["accountApps_syncSlowTitle"], 9000);
      }
    });
  }, [integrations, syncingIds, syncProgress, now]);

  const query = searchText.trim().toLowerCase();
  const rows = useMemo(
    () => integrations.filter((item) => !query || (item.integrationName || "").toLowerCase().includes(query)),
    [integrations, query]
  );

  const columns = useMemo(() => [
    {
      key: "integrationName",
      title: messages["accountApps_name"],
      show: true,
      width: "47%",
      // The name is the design's dropdown with the integration options (edit panel, linked vehicles console)
      render: (row) => (
        <div className={cx("account-apps-name", { "account-apps-flash": row.id === flashId })}>
          <IntegrationMenu integration={row} syncing={syncingIds.includes(row.id)} />
        </div>
      )
    },
    {
      key: "apiKeyMasked",
      title: messages["accountApps_apiKey"],
      show: true,
      width: "28%",
      render: (row) => (
        <span className="account-apps-key">
          {row.apiKeyMasked}
          {row.apiKeyMasked && <CopyApiKey integration={row} />}
        </span>
      )
    },
    {
      key: "status",
      title: messages["accountApps_status"],
      show: true,
      width: "25%",
      render: (row) => {
        // Two concepts: the pill is the integration (Active = its vehicles keep syncing), and apps with telemetry
        // (CoastPay) add their live transmission, read from the transmission log (data-catalog sends while the
        // integration is active). Apps that sync vehicles show how many are linked, which opens the linked vehicles
        // console (design B "N vehicles linked", also while Inactive), except while a sync started here runs
        const active = row.status === "Active";
        const syncing = syncingIds.includes(row.id);
        const transmission = active ? transmissionState(row, messages, now) : null;
        const linked = row.syncsVehicles && !syncing;
        return (
          <div className="account-apps-status">
            <Tooltip title={statusTip(row, active, messages)} placement="top">
              <span className={cx("account-apps-pill", { "account-apps-pill-active": active })}>
                {active ? messages["accountApps_active"] : messages["accountApps_inactive"]}
              </span>
            </Tooltip>
            {
              linked &&
              <Tooltip title={messages["accountApps_openLinked"]} placement="top">
                <button type="button" className="account-apps-linked" onClick={() => dispatch(openAppsConsole(row.id))}>
                  <span className={cx("account-apps-linked-dot", { "is-active": active })} />
                  {linkedText(row.linkedVehicles, messages)}
                </button>
              </Tooltip>
            }
            {
              transmission && !syncing &&
              <Tooltip title={transmission.tip} placement="top">
                <span className={cx(transmission.className, { "account-apps-transmission-plain": linked })}>
                  {!linked && <span className="account-apps-transmission-dot" />}
                  {transmission.label}
                </span>
              </Tooltip>
            }
            {
              syncing &&
              <span className="account-apps-syncing">
                <Icon className="account-apps-spin">sync</Icon>
                {syncText(syncProgress[row.id], messages)}
              </span>
            }
          </div>
        );
      }
    }
  ], [messages, flashId, syncingIds, syncProgress, now]);

  const footer = () => {
    if (!integrations.length) return messages["accountApps_noItems"];
    if (!rows.length) {
      return (
        <span className="account-apps-no-match">
          {messages["accountApps_noMatch"].replace("_SEARCH_", searchText.trim())}
          <button type="button" onClick={() => setSearchText("")}>{messages["accountApps_clearSearch"]}</button>
        </span>
      );
    }
    if (query) {
      return messages["accountApps_footerMatch"].replace("_MATCH_", rows.length).replace("_COUNT_", integrations.length);
    }
    return integrations.length === 1
      ? messages["accountApps_footerOne"]
      : messages["accountApps_footer"].replace("_COUNT_", integrations.length);
  };

  // The console and the edit panel read their integration from the list, which the silent reload keeps fresh
  const consoleIntegration = consoleState.open ? integrations.find((item) => item.id === consoleState.integrationId) : null;
  const editIntegration = edit.open ? integrations.find((item) => item.id === edit.integrationId) : null;

  const canDelete = selectedItems.length > 0;
  const deleteTip = !canDelete
    ? messages["accountApps_deleteSelect"]
    : selectedItems.length > 1
      ? messages["accountApps_deleteMany"].replace("_COUNT_", selectedItems.length)
      : messages["accountApps_deleteOne"];

  return (
    <Container maxWidth={false} className="account-apps">
      <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
        <Toolbar>
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
            <Grid item>
              <div style={{ marginLeft: 6 }}>
                <Button
                  type="icon"
                  icon="add_box"
                  placement="bottom"
                  tooltip={messages["accountApps_add"]}
                  onClick={(e) => {
                    if (e) e.preventDefault();
                    dispatch(openAppsWizard(true));
                  }}
                />
              </div>
            </Grid>
            <Grid item>
              <Button
                type="icon"
                icon="delete"
                placement="bottom"
                tooltip={deleteTip}
                disabled={!canDelete}
                onClick={() => dispatch(openAppsDelete({ open: true, integrations: selectedItems }))}
              />
            </Grid>
          </Grid>
        </Toolbar>
        <Grid item xs={12} sm={6} className="mb-10 text-right">
          <SearchInput
            id="account-apps-search"
            placeholder={messages["accountApps_search"]}
            searchText={searchText}
            onSearchText={setSearchText}
          />
        </Grid>
      </Grid>

      <Table
        ref={tableRef}
        keyColumn="id"
        className="account-apps-table"
        loading={loading}
        topheight={235}
        data={rows}
        columns={columns}
        hideDragable
        infiniteScroll
        total={rows.length}
        onSelectedItems={(items) => setSelectedItems(items)}
        noMoreItemsMessage={footer()}
      />

      {openWizard && <NewIntegrationWizard integrations={integrations} />}
      {openDelete && <DeleteIntegrations />}
      {consoleIntegration && <LinkedVehiclesConsole integration={consoleIntegration} />}
      {/* Remounted when the silent reload brings a newer row, so the panel never edits stale values */}
      {editIntegration && <EditIntegration key={`${editIntegration.id}-${editIntegration.updatedOn}`} integration={editIntegration} />}
    </Container>
  );
};

const withApp = (messages, key, row) => messages[key].replace("_APP_", row.integrationName);

/** "Syncing vehicles · 300 read" once the sync reports the vehicles it read (RUNNING pushes between CoastPay pages) */
const syncText = (progress, messages) => {
  if (!progress?.processed) return messages["accountApps_syncing"];
  return messages["accountApps_syncingCount"].replace("_COUNT_", progress.processed.toLocaleString());
};

/** "12 vehicles linked", "1 vehicle linked" */
const linkedText = (count, messages) => (Number(count) === 1
  ? messages["accountApps_linkedOne"]
  : messages["accountApps_linked"].replace("_COUNT_", (Number(count) || 0).toLocaleString()));

/** Tooltip of the integration pill. */
const statusTip = (row, active, messages) => {
  if (!active) return messages["accountApps_pillInactiveTip"];
  return row.syncsVehicles ? withApp(messages, "accountApps_pillSyncs", row) : messages["accountApps_pillActive"];
};

/**
 * Live transmission of an active app with telemetry (API `telemetry`, from the transmission log): transmitting when
 * there was a transmission in the last minutes, the last one when it is older, or none yet. The tooltip carries
 * today's totals. Null for apps without telemetry.
 */
const transmissionState = (row, messages, now) => {
  const state = telemetryState(row.telemetry, now);
  if (!state) return null;
  const { telemetry } = row;
  const tip = messages["accountApps_telemetryTip"]
    .replace("_SENT_", (telemetry.sentToday || 0).toLocaleString())
    .replace("_ERRORS_", (telemetry.errorsToday || 0).toLocaleString())
    .replace("_TZ_", zoneLabel(telemetry.timeZone));
  if (state === "none") {
    return { className: "account-apps-transmission account-apps-transmission-idle", label: messages["accountApps_noTransmissions"], tip };
  }
  const since = sinceText(telemetry.lastOn, now, telemetry.timeZone, messages);
  return state === "live"
    ? { className: "account-apps-transmission account-apps-transmission-on", label: messages["accountApps_transmittingLast"].replace("_TIME_", since), tip }
    : { className: "account-apps-transmission account-apps-transmission-idle", label: messages["accountApps_lastTransmission"].replace("_TIME_", since), tip };
};

export default injectIntl(AppsTab);
